// Dataset Validation Schema and Utilities
// This module provides strict validation for all uploaded data to prevent Object Injection attacks

import { DataSetT, DataSetTypeT, ColumnDataT } from '@components/dataSets/dataSet.types';

// Debug flag for development and local environments only
const debug: boolean = process.env.REACT_APP_HOST_TYPE === 'dev' || process.env.REACT_APP_HOST_TYPE === 'local';

// Security: Define allowed properties to prevent object injection
const ALLOWED_DATASET_PROPERTIES: (keyof DataSetT)[] = [
  'uuid',
  'createdBy',
  'createdOn', 
  'name',
  'type',
  'dataFormatted',
  'data',
  'nColumns',
  'nRows',
  'columns',
  'compoundIds',
  'sourceIds',
  'userId',
  'plotId',
  'universeId',
  'universeStatus'
];

const ALLOWED_COLUMN_PROPERTIES: (keyof ColumnDataT)[] = [
  'label',
  'value', 
  'type'
];

// Security: Validation error types
export class ValidationError extends Error {
  constructor(
    message: string,
    public field?: string,
    public code?: string
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

// Security: Schema validation functions
export const validateString = (value: unknown, fieldName: string, maxLength = 1000): string => {
  if (typeof value !== 'string') {
    throw new ValidationError(`${fieldName} must be a string`, fieldName, 'INVALID_TYPE');
  }
  if (value.length > maxLength) {
    throw new ValidationError(`${fieldName} exceeds maximum length of ${maxLength}`, fieldName, 'MAX_LENGTH_EXCEEDED');
  }
  // Security: Prevent script injection
  if (/<script|javascript:|data:|vbscript:/i.test(value)) {
    throw new ValidationError(`${fieldName} contains potentially malicious content`, fieldName, 'MALICIOUS_CONTENT');
  }
  return value;
};

export const validateNumber = (value: unknown, fieldName: string, min = 0, max = 1000000): number => {
  if (typeof value !== 'number' || isNaN(value)) {
    throw new ValidationError(`${fieldName} must be a valid number`, fieldName, 'INVALID_TYPE');
  }
  if (value < min || value > max) {
    throw new ValidationError(`${fieldName} must be between ${min} and ${max}`, fieldName, 'OUT_OF_RANGE');
  }
  return value;
};

export const validateDataSetType = (value: unknown): DataSetTypeT => {
  const validTypes: DataSetTypeT[] = ['Generic', 'Dose Response', 'Target Panel'];
  if (!validTypes.includes(value as DataSetTypeT)) {
    throw new ValidationError('Invalid dataset type', 'type', 'INVALID_ENUM');
  }
  return value as DataSetTypeT;
};

export const validateUUID = (value: unknown, fieldName: string): string => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const str = validateString(value, fieldName, 50);
  if (!uuidRegex.test(str)) {
    throw new ValidationError(`${fieldName} must be a valid UUID`, fieldName, 'INVALID_UUID');
  }
  return str;
};

export const validateArray = <T>(
  value: unknown, 
  fieldName: string, 
  validator: (item: unknown, index: number) => T,
  maxLength = 100000
): T[] => {
  if (!Array.isArray(value)) {
    throw new ValidationError(`${fieldName} must be an array`, fieldName, 'INVALID_TYPE');
  }
  if (value.length > maxLength) {
    throw new ValidationError(`${fieldName} exceeds maximum length of ${maxLength}`, fieldName, 'MAX_LENGTH_EXCEEDED');
  }
  return value.map((item, index) => validator(item, index));
};

// Security: Sanitize object by removing non-allowed properties
export const sanitizeObject = <T extends Record<string, any>>(
  obj: unknown,
  allowedProperties: (keyof T)[],
  objectName: string
): Partial<T> => {
  if (!obj || typeof obj !== 'object') {
    throw new ValidationError(`${objectName} must be an object`, objectName, 'INVALID_TYPE');
  }

  const sanitized: Partial<T> = {};
  const objRecord = obj as Record<string, unknown>;

  // Security: Only copy allowed properties
  for (const prop of allowedProperties) {
    const propKey = prop as string;
    if (propKey in objRecord && objRecord[propKey] !== undefined) {
      sanitized[prop] = objRecord[propKey] as T[keyof T];
    }
  }

  // Security: Log and reject if unknown properties found
  const unknownProps = Object.keys(objRecord).filter(key => !allowedProperties.includes(key as keyof T));
  if (unknownProps.length > 0) {
    if (debug) console.warn(`⚠️ SECURITY: Rejected unknown properties in ${objectName}:`, unknownProps);
    throw new ValidationError(
      `Object contains unauthorized properties: ${unknownProps.join(', ')}`, 
      objectName, 
      'UNAUTHORIZED_PROPERTIES'
    );
  }

  return sanitized;
};

// Security: Validate column data
export const validateColumnData = (value: unknown): ColumnDataT => {
  const sanitized = sanitizeObject<ColumnDataT>(value, ALLOWED_COLUMN_PROPERTIES, 'column');
  
  return {
    label: validateString(sanitized.label, 'column.label', 200),
    value: validateString(sanitized.value, 'column.value', 200),
    type: validateString(sanitized.type, 'column.type', 50)
  };
};

// Security: Validate data content (arrays of primitives only)
export const validateDataContent = (value: unknown): any => {
  if (value === null || value === undefined) {
    return null;
  }
  
  if (Array.isArray(value)) {
    return validateArray(value, 'data', (item, index) => {
      if (Array.isArray(item)) {
        // Nested array - validate each element
        return validateArray(item, `data[${index}]`, (subItem) => {
          // Only allow primitives in data
          if (typeof subItem === 'string' || typeof subItem === 'number' || typeof subItem === 'boolean' || subItem === null) {
            return subItem;
          }
          throw new ValidationError(`Data can only contain primitive values (string, number, boolean, null)`, `data[${index}]`, 'INVALID_DATA_TYPE');
        }, 10000);
      } else if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean' || item === null) {
        return item;
      } else if (typeof item === 'object') {
        // Allow simple objects but validate their properties
        const record = item as Record<string, unknown>;
        const validatedRecord: Record<string, any> = {};
        
        for (const [key, val] of Object.entries(record)) {
          // Validate key
          if (typeof key !== 'string' || key.length > 100) {
            throw new ValidationError(`Invalid object key in data`, `data[${index}].${key}`, 'INVALID_KEY');
          }
          // Only allow primitive values
          if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean' || val === null || val === undefined) {
            validatedRecord[key] = val;
          } else {
            throw new ValidationError(`Data objects can only contain primitive values`, `data[${index}].${key}`, 'INVALID_DATA_TYPE');
          }
        }
        return validatedRecord;
      }
      
      throw new ValidationError(`Data can only contain primitive values or simple objects`, `data[${index}]`, 'INVALID_DATA_TYPE');
    }, 100000);
  }
  
  // For non-array data, only allow primitives
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  
  throw new ValidationError('Data must be an array or primitive value', 'data', 'INVALID_DATA_TYPE');
};

// Security: Main dataset validation function
export const validateDataSet = (input: unknown): DataSetT => {
  if (debug) console.log('🔒 SECURITY: Validating dataset with strict schema');
  
  // Security: Sanitize and validate the main object
  const sanitized = sanitizeObject<DataSetT>(input, ALLOWED_DATASET_PROPERTIES, 'dataset');
  
  // Validate required fields
  const validated: DataSetT = {
    uuid: sanitized.uuid ? validateUUID(sanitized.uuid, 'uuid') : '',
    name: validateString(sanitized.name, 'name', 500),
    type: validateDataSetType(sanitized.type),
    data: validateDataContent(sanitized.data),
    nColumns: validateNumber(sanitized.nColumns, 'nColumns', 0, 10000),
    nRows: validateNumber(sanitized.nRows, 'nRows', 0, 1000000),
    columns: validateArray(sanitized.columns || [], 'columns', validateColumnData, 10000),
    
    // Optional fields
    createdBy: sanitized.createdBy ? validateString(sanitized.createdBy, 'createdBy', 100) : undefined,
    createdOn: sanitized.createdOn ? validateString(sanitized.createdOn, 'createdOn', 50) : undefined,
    dataFormatted: sanitized.dataFormatted ? validateArray(sanitized.dataFormatted, 'dataFormatted', validateDataContent, 100000) : undefined,
    compoundIds: sanitized.compoundIds ? validateArray(sanitized.compoundIds, 'compoundIds', (id) => validateString(id, 'compoundId', 100), 10000) : undefined,
    sourceIds: sanitized.sourceIds ? validateArray(sanitized.sourceIds, 'sourceIds', (id) => validateString(id, 'sourceId', 100), 10000) : undefined,
    userId: sanitized.userId ? validateString(sanitized.userId, 'userId', 100) : undefined,
    plotId: sanitized.plotId ? validateString(sanitized.plotId, 'plotId', 100) : undefined,
    universeId: sanitized.universeId ? validateString(sanitized.universeId, 'universeId', 100) : undefined,
    universeStatus: sanitized.universeStatus ? validateString(sanitized.universeStatus, 'universeStatus', 50) : undefined,
  };
  
  if (debug) console.log('✅ SECURITY: Dataset validation successful');
  return validated;
};

// Security: Validate upload payload (for Data.create API)
export interface ValidatedUploadPayload {
  uuid: string;
  name: string;
  type: string;
  data: string; // API expects data as bytes/string
  universeId: string;
  computationStatus: string;
  nColumns?: number;
  nRows?: number;
  columns?: ColumnDataT[];
  [key: string]: any; // Allow additional fields that might be required
}

export const validateUploadPayload = (
  dataSet: DataSetT, 
  universeId: string, 
  computationStatus: string
): ValidatedUploadPayload => {
  if (debug) console.log('🔒 SECURITY: Validating upload payload');
  
  // Validate and sanitize the entire dataset
  const validatedDataSet = validateDataSet(dataSet);
  
  // Create payload with all required fields for API
  const payload: ValidatedUploadPayload = {
    uuid: validatedDataSet.uuid || validateUUID(universeId, 'uuid'), // Use dataset uuid or fallback to universeId
    name: validatedDataSet.name,
    type: validatedDataSet.type,
    data: JSON.stringify(validatedDataSet.data), // Encode data as JSON string
    universeId: validateUUID(universeId, 'universeId'),
    computationStatus: validateString(computationStatus, 'computationStatus', 50),
    nColumns: validatedDataSet.nColumns,
    nRows: validatedDataSet.nRows,
    columns: validatedDataSet.columns
  };
  
  // Add optional fields if they exist
  if (validatedDataSet.createdBy) payload.createdBy = validatedDataSet.createdBy;
  if (validatedDataSet.createdOn) payload.createdOn = validatedDataSet.createdOn;
  if (validatedDataSet.compoundIds) payload.compoundIds = validatedDataSet.compoundIds;
  if (validatedDataSet.sourceIds) payload.sourceIds = validatedDataSet.sourceIds;
  if (validatedDataSet.userId) payload.userId = validatedDataSet.userId;
  
  if (debug) console.log('✅ SECURITY: Upload payload validation successful');
  return payload;
};

// Security: Additional validation for Universe creation
export interface ValidatedUniversePayload {
  uuid: string;
  response_type: string;
  title: string;
  data_uuid: string;
  data: any;
}

export const validateUniversePayload = (
  title: string,
  responseType: string,
  dataSet: DataSetT,
  universeId: string
): ValidatedUniversePayload => {
  if (debug) console.log('🔒 SECURITY: Validating universe payload');
  
  const validatedDataSet = validateDataSet(dataSet);
  
  const payload: ValidatedUniversePayload = {
    uuid: validateUUID(universeId, 'universeId'),
    response_type: validateString(responseType, 'response_type', 50),
    title: validateString(title, 'title', 200),
    data_uuid: validateString(validatedDataSet.uuid, 'data_uuid', 100),
    data: validatedDataSet.data
  };
  
  if (debug) console.log('✅ SECURITY: Universe payload validation successful');
  return payload;
};
